import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { signOut, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      console.log('AdminLayout: Sign out button clicked');
      
      const { error } = await signOut();
      
      if (error) {
        console.error('AdminLayout: Sign out error:', error);
        // Show user-friendly error message
        alert('Väljalogimine ebaõnnestus. Palun proovige uuesti.');
      } else {
        console.log('AdminLayout: Sign out successful, redirecting to login');
        // Force navigation to login page
        navigate('/admin/login', { replace: true });
      }
    } catch (err) {
      console.error('AdminLayout: Sign out exception:', err);
      // Even on error, try to redirect to login
      navigate('/admin/login', { replace: true });
    }
  };

  const sidebarItems = [
    { name: 'Ülevaade', path: '/admin', icon: '📊' },
    { name: 'Avaleht', path: '/admin/hero', icon: '🏠' },
    { name: 'Teenused', path: '/admin/services', icon: '🛠️' },
    { name: 'Meist', path: '/admin/about', icon: '👥' },
    { name: 'Kontakt', path: '/admin/contact', icon: '📞' },
    { name: 'Jalus', path: '/admin/footer', icon: '📄' },
    { name: 'Seaded', path: '/admin/settings', icon: '⚙️' },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <img 
                src="https://i.ibb.co/MyxNs6ZB/forestry.png" 
                alt="Forestry Logo" 
                className="h-8 w-auto mr-4"
              />
              <span className="text-xl font-bold text-primary-700">Forestry CMS</span>
            </div>
            <div className="flex items-center space-x-4">
              {user && (
                <span className="text-sm text-gray-600">
                  Tere, {user.email}
                </span>
              )}
              <Link 
                to="/" 
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-700 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="mr-2">←</span>
                Tagasi kodulehele
              </Link>
              <button
                onClick={handleSignOut}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 rounded-md transition-colors"
                type="button"
              >
                Logi välja
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white shadow-sm min-h-screen">
          <nav className="mt-8">
            <div className="px-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Sisu haldus
              </h3>
              <div className="mt-4 space-y-1">
                {sidebarItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  const isDangerous = item.path === '/admin/clear-database';
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-primary-100 text-primary-700'
                          : isDangerous
                          ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-primary-700'
                      }`}
                    >
                      <span className="mr-3 text-lg">{item.icon}</span>
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;